import {Logger, NumberUtils} from 'neo-common';
import {Balance, FeatureData, GameDataLoaded, IPlatforms, RoundEnded, RoundStarted, SpinReceived} from './IPlatforms';
import {Platforms} from './Platforms';

export class PlatformRelaxFEIM implements IPlatforms {

    private loadStarted: boolean;

    private lastUpdatedBalance: number;

    private lastBet: number;

    private lastRoundId: number;

    private freeRound: boolean;

    private roundType: string;

    private roundTypes = {
        NORMAL: 'normal',
        DEMO: 'demo',
        FREE_ROUNDS: 'freerounds',
        FEATURE_FREE_ROUNDS: 'featurefreerounds'
    };

    public create(): void {
        Platforms.canPlay = true;

        if (DEBUG) {
            this.log('create');
        }

        this.loadStarted = false;
        this.createPlugin();
    }

    public loadProgress(progress: number): void {
        if (DEBUG) {
            this.log('gameReady');
        }

        if ((window['FEIM'] as any) != null) {
            if (!this.loadStarted) {
                this.loadStarted = true;
                (window['FEIM'] as any).send.gameLoadStarted();
            }

            (window['FEIM'] as any).send.gameLoadProgress(progress);
        }
    }

    public gameReady(): void {
        if (DEBUG) {
            this.log('gameReady');
        }

        if ((window['FEIM'] as any) != null) {
            (window['FEIM'] as any).send.gameLoadCompleted();
        }
    }

    public gameFailed(): void {}

    public gameClosing(): void {
        if (DEBUG) {
            this.log('gameClosing');
        }

        if ((window['FEIM'] as any) != null) {
            (window['FEIM'] as any).send.exitGame();
        }
    }

    public gameReloading(): void {}

    public gameChange(gameCode: string): void {}

    public error(code: string, message: string): void {
        if (DEBUG) {
            this.log('error');
        }

        if ((window['FEIM'] as any) != null) {

            //check if operator handles error
            const configuration = (window['FEIM'] as any).getConfiguration() as any;
            if (configuration != null) {
                const operatorHandlesErrors = configuration['operatorHandlesErrors'];
                if (operatorHandlesErrors) {
                    Platforms.handleShowErrorDialog = true;
                }
            }

            (window['FEIM'] as any).send.errorMessage({
                'errorCode': code,
                'errorMessage': message
            });
        }
    }

    public clientError(id: string, message: string): void {}

    public roundInit(): void {}

    public roundStarted(args?: RoundStarted): void {
        if (DEBUG) {
            this.log('roundStarted');
        }

        if (args != null) {
            this.lastBet = args.totalBet;
            this.freeRound = args.freeRound;
        } else {
            this.lastBet = null;
            this.freeRound = false;
        }

        if ((window['FEIM'] as any) != null) {
            (window['FEIM'] as any).send.roundStarted({balance: this.lastUpdatedBalance});
        }
    }

    public spinStarted(): void {}

    public spinReceived(args: SpinReceived): void {
        if (args != null) {
            if (args.roundId != null) {
                this.lastRoundId = args.roundId;
            }
        }
    }

    public spinEnded(): void {}

    public roundEnded(args?: RoundEnded): void {
        if (DEBUG) {
            this.log('roundEnded');
        }

        this.roundType = this.roundTypes.NORMAL;

        if (this.freeRound) {
            this.roundType = this.roundTypes.FREE_ROUNDS;
        }

        if (this.lastRoundId === -1) {
            this.roundType = this.roundTypes.DEMO;
        }

        if ((window['FEIM'] as any) != null && args != null) {
            (window['FEIM'] as any).send.roundFinished({
                balance: parseInt(NumberUtils.multiply(args.balanceAfter, 100)),
                win: {win: parseInt(NumberUtils.multiply(args.win, 100))},
                bet: this.lastBet ? parseInt(NumberUtils.multiply(this.lastBet, 100)) : undefined,
                playResponse: {roundType: this.roundType}
            });
        }
    }

    public featureStarted(args?: FeatureData): void {
        if (DEBUG) {
            this.log('featureStarted');
        }

        if ((window['FEIM'] as any) != null && args != null) {
            (window['FEIM'] as any).send.featureStarted({'type': args.type});
        }
    }

    public featureInit(): void {}

    public featureFinished(args?: FeatureData): void {
        if (DEBUG) {
            this.log('featureFinished');
        }

        if ((window['FEIM'] as any) != null && args != null) {
            (window['FEIM'] as any).send.featureFinished({'type': args.type});
        }
    }

    public autoplayStarted(): void {
        if (DEBUG) {
            this.log('autoplayStarted');
        }

        if ((window['FEIM'] as any) != null) {
            (window['FEIM'] as any).send.autoPlayStarted();
        }
    }

    public autoplayFinished(): void {
        if (DEBUG) {
            this.log('autoplayFinished');
        }

        if ((window['FEIM'] as any) != null) {
            (window['FEIM'] as any).send.autoPlayFinished();
        }
    }

    public gotoCasier(): void {
        if (DEBUG) {
            this.log('gotoCasier');
        }

        if ((window['FEIM'] as any) != null) {
            (window['FEIM'] as any).send.openQuickDeposit();
        }
    }

    public gotoHistory(): void {
        if (DEBUG) {
            this.log('gotoHistory');
        }

        if ((window['FEIM'] as any) != null) {
            (window['FEIM'] as any).send.showHistory();
        }
    }

    public gotoHelp(): void {}

    public gotoPaytable(): void {}

    public balanceUpdated(args?: Balance): void {
        if (DEBUG) {
            this.log('balanceUpdated');
        }

        if ((window['FEIM'] as any) != null && args != null) {
            this.lastUpdatedBalance = parseInt(NumberUtils.multiply(args.amount, 100));
            (window['FEIM'] as any).send.balanceUpdate(this.lastUpdatedBalance);
        }
    }

    public sendPluginMessage(message: string): void {}

    public setVolume(level: number): void {
        if (DEBUG) {
            this.log('setVolume');
        }

        if ((window['FEIM'] as any) != null) {
            (window['FEIM'] as any).send.updateSettings({
                'sounds': level > 0
            });
        }
    }

    public quickSpinEnabled(enabled: boolean): void {
        if (DEBUG) {
            this.log('quickSpinEnabled');
        }

        if ((window['FEIM'] as any) != null) {
            (window['FEIM'] as any).send.updateSettings({
                'fastPlay': enabled
            });
        }
    }

    public bet(value: number): void {
        if (DEBUG) {
            this.log('bet');
        }

        if ((window['FEIM'] as any) != null) {
            (window['FEIM'] as any).send.betUpdate(parseInt(NumberUtils.multiply(value, 100)));
        }
    }

    public win(value: number, cumulative?: boolean): void {
        if (DEBUG) {
            this.log('win');
        }

        if ((window['FEIM'] as any) != null) {
            if (cumulative) {
                return;
            }

            (window['FEIM'] as any).send.winUpdate({win: parseInt(NumberUtils.multiply(value, 100))});
        }
    }

    public userAction(action: string, data: Object): void {
        if (DEBUG) {
            this.log('userAction');
        }

        if (action === 'exit') {
            if (data['url'] != null) {
                if ((window['FEIM'] as any) != null) {
                    (window['FEIM'] as any).send.navigate(data['url']);
                }
            }
        }
    }

    public gameDataLoaded(args: GameDataLoaded): void {
        if ((window['FEIM'] as any) != null && args != null) {
            //update configuration
            (window['FEIM'] as any).configure({
                p2pConfig: {currency: args.CurrencyCode}
            });

            if (args.Balance != null) {
                this.balanceUpdated({amount: args.Balance});
            }

            if (args.Bet != null) {
                this.bet(args.Bet);
            }
        }
    }

    public dialogOpened(type: string, isOpen: boolean): void {}

    public fullscreen(enabled: boolean): void {}

    private createPlugin(): void {
        if (DEBUG) {
            this.log('createPlugin');
        }

        if (Platforms.pluginUrl != null) {
            let script = document.createElement('script');
            script.setAttribute('type', 'text/javascript');
            script.setAttribute('src', Platforms.pluginUrl);
            script.onload = this.loadPlugin.bind(this);
            document.head.appendChild(script);
        }
    }

    private loadPlugin(): void {
        if (DEBUG) {
            this.log('loadPlugin');
        }

        let self = this;

        if ((window['FEIM'] as any) != null) {
            //initial configuration
            (window['FEIM'] as any).configure({
                handleRgPostMessageAPI: true
            });

            (window['FEIM'] as any).on.refreshBalance(this.updateBalance.bind(this));
            (window['FEIM'] as any).on.freeze(this.freeze.bind(this));
            (window['FEIM'] as any).on.unfreeze(this.unfreeze.bind(this));
            (window['FEIM'] as any).on.pauseAutoPlay(this.pauseAutoPlay.bind(this));
            (window['FEIM'] as any).on.errorMessageDismissed(this.errorMessageDismissed.bind(this));
            (window['FEIM'] as any).on.toggleGameHelp((action: any) => {
                self.toggleGameHelp(action);
            });
            (window['FEIM'] as any).on.togglePaytable((action: any) => {
                self.togglePaytable(action);
            });
            (window['FEIM'] as any).on.updateSettings((data: any) => {
                self.updateSettings(data);
            });
            // (window['FEIM'] as any).on.exitingGame(); //unnecessary

            window.addEventListener('message', (event) => {
                if (event != null) {
                    let data = event.data;
                    if (data != null && data['feimOpEvent'] != null) {
                        if (data['feimOpEvent'] === 'pause-when-idle') {
                            self.pauseWhenIdle();
                        }
                    }
                }
            });
        }

        Platforms.canPlay = true;
    }

    private freeze(): void {
        if (DEBUG) {
            this.log('freeze');
        }

        Platforms.gamePlayFreeze();
    }

    private unfreeze(): void {
        if (DEBUG) {
            this.log('unfreeze');
        }

        Platforms.gamePlayUnfreeze();
    }

    private pauseWhenIdle(): void {
        if (DEBUG) {
            this.log('pauseWhenIdle');
        }

        Platforms.gamePlaySupressSpinOnCollect();
    }

    private pauseAutoPlay(): void {
        if (DEBUG) {
            this.log('pauseAutoPlay');
        }

        Platforms.stopAutoplay();
    }

    private toggleGameHelp(action: any): void {
        if (DEBUG) {
            this.log('toggleGameHelp');
        }

        if (action != null && action['type'] != null) {
            switch(action['type']) {
                case 'show':
                    Platforms.showHelp();
                    break;
                case 'hide':
                    Platforms.hideHelp();
                    break;
                default:
                    Platforms.toggleHelp();
            }
        } else {
            Platforms.toggleHelp();
        }
    }

    private togglePaytable(action: any): void {
        if (DEBUG) {
            this.log('togglePaytable');
        }

        if (action != null && action['type'] != null) {
            switch(action['type']) {
                case 'show':
                    Platforms.showPaytable();
                    break;
                case 'hide':
                    Platforms.hidePaytable();
                    break;
                default:
                    Platforms.togglePaytable();
            }
        } else {
            Platforms.togglePaytable();
        }
    }

    private updateSettings(data: any): void {
        if (DEBUG) {
            this.log('updateSettings');
        }

        if (data != null) {
            if (data['fastPlay'] != null) {
                Platforms.gameQuickSpin(data['fastPlay']);
            }

            if (data['sounds'] != null) {
                if(data['sounds']) {
                    Platforms.gameAudioEnable();
                } else {
                    Platforms.gameAudioMute();
                }
            }
        }
    }

    private errorMessageDismissed(): void {
        if (DEBUG) {
            this.log('errorMessageDismissed');
        }

        Platforms.platformErrorMessageDismissed();
    }

    private updateBalance(): void {
        if (DEBUG) {
            this.log('updateBalance');
        }

        Platforms.refreshBalance();
    }

    protected log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup('Relax', 'Relax ' + s);
        }
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/Platforms/PlatformRelaxFEIM.ts
 **/